import { useCallback, useRef, useState } from 'react';
import { cx } from '@/styled-system/css';
import type { Photo } from 'app/types/commonTypes.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { buildListingPhotoAlt } from 'app/shared/utils/listingUtils';
import { DesktopGalleryButtons } from './DesktopGalleryButtons';
import { ImageCounter } from './ImageCounter';
import { MobileGalleryButtons } from './MobileGalleryButtons';
import * as styles from './photoGallery.styles';
import { VerifiedBadge } from './VerifiedBadge';

type PhotoGalleryProps = {
  photos: Array<Photo>;
  displayName?: string;
  onAllPhotosClick: () => void;
  onGalleryTap?: () => void;
  initialIndex?: number;
  has3dTour?: boolean;
  hasVideos?: boolean;
  hasStreetView?: boolean;
  on3dTourClick?: () => void;
  onVideosClick?: () => void;
  onStreetViewClick?: () => void;
  trusted?: boolean;
};

const SWIPE_THRESHOLD = 50;

const getBentoGridModifier = (photoCount: number): string => {
  switch (photoCount) {
    case 1:
      return styles.desktopBentoGrid1;
    case 2:
      return styles.desktopBentoGrid2;
    case 3:
      return styles.desktopBentoGrid3;
    default:
      return styles.desktopBentoGrid4;
  }
};

export const PhotoGallery = ({
  photos,
  displayName,
  onAllPhotosClick,
  onGalleryTap,
  initialIndex = 0,
  has3dTour,
  hasVideos,
  hasStreetView,
  on3dTourClick,
  onVideosClick,
  onStreetViewClick,
  trusted,
}: PhotoGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const directionRef = useRef<'horizontal' | 'vertical' | null>(null);
  const didDragRef = useRef(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, photos.length - 1));
  }, [photos.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    startXRef.current = clientX;
    startYRef.current = clientY;
    directionRef.current = null;
    didDragRef.current = false;
    setIsDragging(true);
    setDragOffset(0);
  }, []);

  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging) return;

      const deltaX = clientX - startXRef.current;
      const deltaY = clientY - startYRef.current;

      if (directionRef.current === null) {
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          directionRef.current = Math.abs(deltaX) >= Math.abs(deltaY) ? 'horizontal' : 'vertical';
          didDragRef.current = true;
        }
        return;
      }

      if (directionRef.current !== 'horizontal') return;

      setDragOffset(deltaX);
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (directionRef.current === 'horizontal') {
      if (dragOffset < -SWIPE_THRESHOLD) {
        goToNext();
      } else if (dragOffset > SWIPE_THRESHOLD) {
        goToPrev();
      }
    }

    setDragOffset(0);
  }, [isDragging, dragOffset, goToNext, goToPrev]);

  const handleCarouselClick = useCallback(() => {
    if (!didDragRef.current) {
      onGalleryTap?.();
    }
  }, [onGalleryTap]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    },
    [goToNext, goToPrev],
  );

  if (photos.length === 0) return null;

  const containerWidth = containerRef.current?.offsetWidth || 0;
  const baseOffset = -(currentIndex * containerWidth);
  const translateX = baseOffset + (isDragging ? dragOffset : 0);

  const bentoPhotos = photos.slice(0, 4);

  return (
    <div className={styles.galleryContainer}>
      {/* Mobile: swipe carousel */}
      <div
        ref={containerRef}
        className={styles.mobileCarouselWrapper}
        role="region"
        aria-roledescription="carousel"
        aria-label="Property photos"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleCarouselClick}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          handleDragStart(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => handleDragMove(e.clientX, e.clientY)}
        onPointerUp={handleDragEnd}
        onPointerCancel={() => {
          if (isDragging) {
            setIsDragging(false);
            setDragOffset(0);
          }
        }}
      >
        <div
          className={cx(styles.galleryTrack, isDragging && styles.galleryTrackDragging)}
          style={{ transform: `translateX(${translateX}px)` }}
        >
          {photos.map((photo, i) => {
            const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);

            return (
              <div key={i} className={styles.gallerySlide}>
                {imageAttrs ? (
                  <img
                    src={imageAttrs.src}
                    srcSet={imageAttrs.srcSet}
                    sizes="100vw"
                    alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                    className={styles.galleryImage}
                    loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                ) : (
                  <img
                    src={photo.url}
                    alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                    className={styles.galleryImage}
                    loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                    draggable={false}
                  />
                )}
              </div>
            );
          })}
        </div>

        <ImageCounter
          currentIndex={currentIndex}
          totalCount={photos.length}
          onClick={onGalleryTap ?? onAllPhotosClick}
        />
        <MobileGalleryButtons
          hasVideos={hasVideos}
          has3dTour={has3dTour}
          hasStreetView={hasStreetView}
          onVideosClick={onVideosClick}
          on3dTourClick={on3dTourClick}
          onStreetViewClick={onStreetViewClick}
        />
      </div>

      {/* Desktop: bentobox grid */}
      <div
        className={cx(styles.desktopBentoGridBase, getBentoGridModifier(bentoPhotos.length))}
        role="region"
        aria-label="Property photos"
      >
        {bentoPhotos.map((photo, i) => {
          const imageAttrs = buildResponsiveImageAttrs(photo.url, 768, [384, 768, 1152]);

          return (
            <button
              key={i}
              className={styles.bentoButton}
              onClick={onAllPhotosClick}
              aria-label={`View photo ${i + 1} - See all photos`}
            >
              {imageAttrs ? (
                <img
                  src={imageAttrs.src}
                  srcSet={imageAttrs.srcSet}
                  sizes="50vw"
                  alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                  className={styles.bentoImage}
                />
              ) : (
                <img
                  src={photo.url}
                  alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                  className={styles.bentoImage}
                />
              )}
            </button>
          );
        })}

        <DesktopGalleryButtons
          onAllPhotosClick={onAllPhotosClick}
          has3dTour={has3dTour}
          hasVideos={hasVideos}
          hasStreetView={hasStreetView}
          on3dTourClick={on3dTourClick}
          onVideosClick={onVideosClick}
          onStreetViewClick={onStreetViewClick}
        />
      </div>

      {trusted && <VerifiedBadge hasOtherMobileBadges={Boolean(has3dTour || hasVideos || hasStreetView)} />}
    </div>
  );
};
